import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Types
import {
  getVehicleOwnershipLabel,
  getVehicleUsageLabel,
} from '../../../../../../types/vehicles';

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getFilteredRowModel,
  createColumnHelper,
  Row,
} from '@tanstack/react-table';

// Mocks

// Types
import {
  TableProps,
  activeVehiclesColumnNames,
  inactiveVehiclesColumnNames,
  dataTable,
  SearchbarFilterTypes,
} from './types';

// Styled components
import {
  ContainerTable,
  FiltersContainer,
  InactiveVehiclesTableContainer,
  ActiveVehiclesTableContainer,
  SwapVehicleButton,
  SwapVehicleButtonLeft,
  InputRadioStyled,
  TrStyled,
} from './styles';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../../Typography';

import { activeVehiclesMocksData, inactiveVehiclesMocksData } from './mocks';
import SearchBar from '../../../../../inputs/SearchBar';
import { matchesSearch } from '../../../../../../utils/textSearch';

export const VehiclesManagementTable = ({
  data,
  onAddInactiveVehicle,
  onAddActiveVehicle,
}: TableProps): JSX.Element => {
  const { t } = useTranslation('forms');
  const activeVehiclesDataAll = data?.activeVehicles ?? activeVehiclesMocksData;
  const inactiveVehiclesDataAll =
    data?.inactiveVehicles ?? inactiveVehiclesMocksData;

  const [activeVehiclesData, setActiveVehiclesData] = useState(
    activeVehiclesDataAll
  );
  const [inactiveVehiclesData, setInactiveVehiclesData] = useState(
    inactiveVehiclesDataAll
  );

  const [activeVehiclesChecked, setActiveVehiclesChecked] = useState({});
  const [inactiveVehiclesChecked, setInactiveVehiclesChecked] = useState({});

  const [filteredActiveVehicles, setFilteredActiveVehicles] = useState(false);
  const [filteredinactiveVehicles, setFilteredinactiveVehicles] =
    useState(false);

  const activeVehiclesColumnHelper = createColumnHelper<dataTable>();
  const inactiveVehiclesColumnHelper = createColumnHelper<dataTable>();

  useEffect(() => {
    setActiveVehiclesData(activeVehiclesDataAll);
    setInactiveVehiclesData(inactiveVehiclesDataAll);
  }, [data]);

  const activeVehiclesColumns = useMemo(
    () => [
      {
        enableHiding: true,
        id: 'select',
        cell: ({ row }: any) => (
          <InputRadioStyled
            name="activeRadioInput"
            {...{
              checked: row.getIsSelected(), // Returns whether or not the row is selected.
              disabled: !row.getCanSelect(), // Returns whether or not the row can be selected.
              indeterminate: row.getIsSomeSelected(), //Returns whether or not some of the row's sub rows are selected.
              onChange: row.getToggleSelectedHandler(), //Returns a handler that can be used to toggle the row.
            }}
          />
        ),
      },
      activeVehiclesColumnHelper.accessor((row) => row.plate, {
        id: 'plate',
        header: () => t('management.vehicleTableHeaders.plate'),
        cell: (info) => info.getValue(),
      }),
      activeVehiclesColumnHelper.accessor((row) => row.model, {
        id: 'model',
        header: () => t('management.vehicleTableHeaders.model'),
      }),
      activeVehiclesColumnHelper.accessor((row) => row.category, {
        id: 'category',
        header: () => t('management.vehicleTableHeaders.category'),
      }),
      activeVehiclesColumnHelper.accessor((row) => row.concession, {
        id: 'concession',
        header: () => t('management.vehicleTableHeaders.concession'),
      }),
      // ✅ NUEVO: Mostrar ownership y usage
      activeVehiclesColumnHelper.accessor((row) => row.ownership, {
        id: 'ownership',
        header: () => 'Propiedad',
        cell: (info) => getVehicleOwnershipLabel(info.getValue() as any),
      }),
      activeVehiclesColumnHelper.accessor((row) => row.usage, {
        id: 'usage',
        header: () => 'Uso',
        cell: (info) => getVehicleUsageLabel(info.getValue() as any),
      }),
      // ✅ M2M: Mostrar equipos
      activeVehiclesColumnHelper.accessor((row) => row.teams, {
        id: 'teams',
        header: () => 'Equipos',
        cell: (info) => {
          const teams = info.getValue();
          return teams && teams.length > 0 ? teams.join(', ') : '-';
        },
      }),
    ],
    [
      activeVehiclesData,
      inactiveVehiclesData,
      activeVehiclesChecked,
      inactiveVehiclesChecked,
      data,
      t,
    ]
  );

  const inactiveVehiclesColumns = useMemo(
    () => [
      {
        enableHiding: true,
        id: 'select',
        cell: ({ row }: any) => (
          <InputRadioStyled
            name="inactiveRadioInput"
            {...{
              checked: row.getIsSelected(), // Returns whether or not the row is selected.
              disabled: !row.getCanSelect(), // Returns whether or not the row can be selected.
              indeterminate: row.getIsSomeSelected(), //Returns whether or not some of the row's sub rows are selected.
              onChange: row.getToggleSelectedHandler(), //Returns a handler that can be used to toggle the row.
            }}
          />
        ),
      },
      inactiveVehiclesColumnHelper.accessor((row) => row.plate, {
        id: 'plate',
        header: () => t('management.vehicleTableHeaders.plate'),
        cell: (info) => info.getValue(),
      }),
      inactiveVehiclesColumnHelper.accessor((row) => row.model, {
        id: 'model',
        header: () => t('management.vehicleTableHeaders.model'),
        cell: (info) => info.getValue(),
      }),
      inactiveVehiclesColumnHelper.accessor((row) => row.category, {
        id: 'category',
        header: () => t('management.vehicleTableHeaders.category'),
        cell: (info) => info.getValue(),
      }),
      inactiveVehiclesColumnHelper.accessor((row) => row.concession, {
        id: 'concession',
        header: () => t('management.vehicleTableHeaders.concession'),
        cell: (info) => info.getValue(),
      }),
      // ✅ NUEVO: Mostrar ownership y usage
      inactiveVehiclesColumnHelper.accessor((row) => row.ownership, {
        id: 'ownership',
        header: () => 'Propiedad',
        cell: (info) => getVehicleOwnershipLabel(info.getValue() as any),
      }),
      inactiveVehiclesColumnHelper.accessor((row) => row.usage, {
        id: 'usage',
        header: () => 'Uso',
        cell: (info) => getVehicleUsageLabel(info.getValue() as any),
      }),
      // ✅ M2M: Mostrar equipos
      inactiveVehiclesColumnHelper.accessor((row) => row.teams, {
        id: 'teams',
        header: () => 'Equipos',
        cell: (info) => {
          const teams = info.getValue();
          return teams && teams.length > 0 ? teams.join(', ') : '-';
        },
      }),
    ],
    [
      activeVehiclesData,
      inactiveVehiclesData,
      activeVehiclesChecked,
      inactiveVehiclesChecked,
      data,
      t,
    ]
  );

  const activeVehiclesSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setActiveVehiclesData(activeVehiclesDataAll);
        setFilteredActiveVehicles((prev) => !prev);
      } else {
        const filtered = activeVehiclesDataAll.filter(
          (vehicle) =>
            matchesSearch([vehicle.plate, vehicle.model, vehicle.category], value)
        );
        setActiveVehiclesData(filtered);
      }
    },
    [activeVehiclesDataAll, filteredActiveVehicles]
  );

  const inactiveVehiclesSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setInactiveVehiclesData(inactiveVehiclesDataAll);
        setFilteredinactiveVehicles((prev) => !prev);
      } else {
        const filtered = inactiveVehiclesDataAll.filter(
          (vehicle) =>
            matchesSearch([vehicle.plate, vehicle.model, vehicle.category], value)
        );
        setInactiveVehiclesData(filtered);
      }
    },
    [inactiveVehiclesDataAll, filteredinactiveVehicles]
  );

  const handleRowClick = (row: Row<dataTable>) => {
    row.getToggleSelectedHandler()(row);
  };

  const addActiveVehicle = () => {
    const selectedRow =
      activeVehiclesTable.getSelectedRowModel().rows[0].original;

    onAddActiveVehicle(selectedRow.id);

    activeVehiclesTable.resetRowSelection(true);
  };

  const addInactiveVehicle = () => {
    const selectedRow =
      inactiveVehiclesTable.getSelectedRowModel().rows[0].original;

    onAddInactiveVehicle(selectedRow.id);

    inactiveVehiclesTable.resetRowSelection(true);
  };

  const activeVehiclesTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: activeVehiclesData,
    columns: activeVehiclesColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveVehiclesChecked,
    state: {
      rowSelection: activeVehiclesChecked,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const inactiveVehiclesTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: inactiveVehiclesData,
    columns: inactiveVehiclesColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveVehiclesChecked,
    state: {
      rowSelection: inactiveVehiclesChecked,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  return (
    <ContainerTable>
      <ActiveVehiclesTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.activeVehicles')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => activeVehiclesSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {activeVehiclesTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {activeVehiclesTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={!activeVehiclesTable.getIsSomeRowsSelected()}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <SwapVehicleButtonLeft onClick={addActiveVehicle}>
          ⮕
        </SwapVehicleButtonLeft>
      </ActiveVehiclesTableContainer>
      <InactiveVehiclesTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.inactiveVehicles')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => inactiveVehiclesSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {inactiveVehiclesTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {inactiveVehiclesTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={!inactiveVehiclesTable.getIsSomeRowsSelected()}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <SwapVehicleButton onClick={addInactiveVehicle}>⬅</SwapVehicleButton>
      </InactiveVehiclesTableContainer>
    </ContainerTable>
  );
};
